class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.floatingChatObserver = null;
    this.floatingChatInterval = null;
    this.floatingChatHideUsesIdle = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setupDrawerAddButtons();
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.normalizeCheckoutButton();
    this.applyRoohDiscountCode();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'ab-native-cart-drawer-open', 'ab-cart-drawer-open');
    this.startFloatingChatHide();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'ab-native-cart-drawer-open', 'ab-cart-drawer-open');
    this.stopFloatingChatHide();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    drawerInner?.classList.contains('is-empty') && drawerInner.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections || !parsedState.sections[section.id]) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.normalizeCheckoutButton();
      this.setupDrawerAddButtons();
      this.open();
    });
  }

  normalizeCheckoutButton() {
    const checkoutTitle = this.querySelector('.gokwik-checkout button > span.btn-text > span:first-child');
    if (checkoutTitle) checkoutTitle.textContent = 'Checkout Now \u2192';
  }

  setupDrawerAddButtons() {
    if (this.dataset.abDrawerAddSetup === 'true') return;
    this.dataset.abDrawerAddSetup = 'true';
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-drawer-add-variant]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.addDrawerVariant(button);
    });
  }

  addDrawerVariant(button) {
    const variantId = button?.dataset?.cartDrawerAddVariant;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart add failed');
        return response.json();
      })
      .then((parsedState) => {
        this.renderCartSections(parsedState.sections || {});
        this.normalizeCheckoutButton();
        this.setupDrawerAddButtons();
        this.open(button);
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  renderCartSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      if (!sections || !sections[section.id]) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });

    this.classList.remove('is-empty');
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  applyRoohDiscountCode() {
    const progressBox = this.querySelector('.cart-drawer__progress-box[data-rooh-discount-code]');
    const code = progressBox?.dataset?.roohDiscountCode?.trim();
    if (!code || this.dataset.abRoohDiscountApplying === code) return;

    const storageKey = `ab_rooh_discount_applied_${code}`;
    try {
      if (window.sessionStorage?.getItem(storageKey) === 'true') return;
      window.sessionStorage?.setItem(storageKey, 'true');
    } catch (error) {}

    this.dataset.abRoohDiscountApplying = code;

    fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart`, {
      credentials: 'same-origin',
    })
      .then(() =>
        fetch(`${routes.cart_url}?sections=${this.getSectionsToRender().map((section) => section.id).join(',')}`, {
          credentials: 'same-origin',
        })
      )
      .then((response) => {
        if (!response.ok) throw new Error('Cart refresh failed');
        return response.json();
      })
      .then((sections) => {
        this.renderCartSections(sections || {});
        this.normalizeCheckoutButton();
        this.setupDrawerAddButtons();
      })
      .catch(() => {
        try {
          window.sessionStorage?.removeItem(storageKey);
        } catch (error) {}
      })
      .finally(() => {
        if (this.dataset.abRoohDiscountApplying === code) delete this.dataset.abRoohDiscountApplying;
      });
  }

  getFloatingChatSelectors() {
    return [
      '.whatsapp-button',
      '.whatsapp_float',
      '.whatsapp-float',
      '.floating-whatsapp',
      '.floating-whatsapp-button',
      '.wa__btn_popup',
      '.wa__popup_chat_box',
      '.joinchat',
      '.joinchat__button',
      '.chaty-widget',
      '.chaty-widget-i',
      '.wati-widget',
      '.zsiq_floatmain',
      '.zsiq_float',
      '.wpp-float',
      'gty-whatsapp-chat-button',
      '.whatsapp-us',
      '[class*="whats" i]',
      '[id*="whats" i]',
      '[class*="whatsapp" i]',
      '[id*="whatsapp" i]',
      'a[href*="wa.me"]',
      'a[href*="api.whatsapp"]',
      'a[href*="web.whatsapp"]',
      'a[href*="whatsapp" i]',
      'iframe[src*="whatsapp"]',
      'iframe[title*="whatsapp" i]',
      'img[src*="whatsapp" i]',
      'img[alt*="whatsapp" i]',
      '[aria-label*="whatsapp" i]',
      '[title*="whatsapp" i]',
    ].join(',');
  }

  getFloatingChatHideTarget(element) {
    if (!element || this.contains(element)) return null;
    if (element.closest('cart-drawer, [class*="gokwik" i], [id*="gokwik" i], #shopify-section-sections--')) return null;

    const tagName = (element.tagName || '').toLowerCase();
    if (['html', 'body', 'script', 'style', 'link', 'meta', 'svg', 'path'].includes(tagName)) return null;

    let node = element;
    while (node && node !== document.body && node !== document.documentElement) {
      if (this.contains(node)) return null;
      const style = window.getComputedStyle(node);
      if (style.position === 'fixed' || style.position === 'sticky') return node;
      node = node.parentElement;
    }

    if (tagName === 'img') return element.closest('a, button, div, iframe') || element;
    return element;
  }

  getFloatingBottomRightElements() {
    return [];
  }

  forceHideFloatingChat(element) {
    if (!element || this.contains(element)) return;
    if (!element.dataset.abNativeOriginalDisplay) element.dataset.abNativeOriginalDisplay = element.style.display || '__empty__';
    if (!element.dataset.abNativeOriginalVisibility)
      element.dataset.abNativeOriginalVisibility = element.style.visibility || '__empty__';
    if (!element.dataset.abNativeOriginalOpacity) element.dataset.abNativeOriginalOpacity = element.style.opacity || '__empty__';
    if (!element.dataset.abNativeOriginalPointerEvents)
      element.dataset.abNativeOriginalPointerEvents = element.style.pointerEvents || '__empty__';
    if (!element.dataset.abNativeOriginalTransform) element.dataset.abNativeOriginalTransform = element.style.transform || '__empty__';
    if (!element.dataset.abNativeOriginalZIndex) element.dataset.abNativeOriginalZIndex = element.style.zIndex || '__empty__';
    if (!element.dataset.abNativeOriginalWidth) element.dataset.abNativeOriginalWidth = element.style.width || '__empty__';
    if (!element.dataset.abNativeOriginalHeight) element.dataset.abNativeOriginalHeight = element.style.height || '__empty__';
    if (!element.dataset.abNativeOriginalOverflow) element.dataset.abNativeOriginalOverflow = element.style.overflow || '__empty__';

    element.classList.add('ab-force-hidden-floating-chat');
    element.style.setProperty('display', 'none', 'important');
    element.style.setProperty('visibility', 'hidden', 'important');
    element.style.setProperty('opacity', '0', 'important');
    element.style.setProperty('pointer-events', 'none', 'important');
    element.style.setProperty('transform', 'scale(0)', 'important');
    element.style.setProperty('z-index', '-2147483648', 'important');
    element.style.setProperty('width', '0', 'important');
    element.style.setProperty('height', '0', 'important');
    element.style.setProperty('overflow', 'hidden', 'important');
  }

  hideFloatingChats() {
    const targets = new Set();
    try {
      document.querySelectorAll(this.getFloatingChatSelectors()).forEach((element) => {
        const target = this.getFloatingChatHideTarget(element);
        if (target) targets.add(target);
      });
    } catch (error) {}

    this.getFloatingBottomRightElements().forEach((element) => targets.add(element));
    targets.forEach((element) => this.forceHideFloatingChat(element));
  }

  restoreFloatingChats() {
    document.querySelectorAll('[data-ab-native-original-display]').forEach((element) => {
      const restore = (property, value) => {
        if (value === '__empty__') element.style.removeProperty(property);
        else element.style.setProperty(property, value);
      };

      restore('display', element.dataset.abNativeOriginalDisplay);
      restore('visibility', element.dataset.abNativeOriginalVisibility);
      restore('opacity', element.dataset.abNativeOriginalOpacity);
      restore('pointer-events', element.dataset.abNativeOriginalPointerEvents);
      restore('transform', element.dataset.abNativeOriginalTransform);
      restore('z-index', element.dataset.abNativeOriginalZIndex);
      restore('width', element.dataset.abNativeOriginalWidth);
      restore('height', element.dataset.abNativeOriginalHeight);
      restore('overflow', element.dataset.abNativeOriginalOverflow);

      element.classList.remove('ab-force-hidden-floating-chat');
      delete element.dataset.abNativeOriginalDisplay;
      delete element.dataset.abNativeOriginalVisibility;
      delete element.dataset.abNativeOriginalOpacity;
      delete element.dataset.abNativeOriginalPointerEvents;
      delete element.dataset.abNativeOriginalTransform;
      delete element.dataset.abNativeOriginalZIndex;
      delete element.dataset.abNativeOriginalWidth;
      delete element.dataset.abNativeOriginalHeight;
      delete element.dataset.abNativeOriginalOverflow;
    });
  }

  startFloatingChatHide() {
    if (this.floatingChatInterval) {
      if (this.floatingChatHideUsesIdle && 'cancelIdleCallback' in window) window.cancelIdleCallback(this.floatingChatInterval);
      else clearTimeout(this.floatingChatInterval);
    }
    const runFallbackHide = () => this.hideFloatingChats();
    this.floatingChatHideUsesIdle = false;
    if ('requestIdleCallback' in window) {
      this.floatingChatHideUsesIdle = true;
      this.floatingChatInterval = window.requestIdleCallback(runFallbackHide, { timeout: 500 });
    } else {
      this.floatingChatInterval = setTimeout(runFallbackHide, 150);
    }
  }

  stopFloatingChatHide() {
    if (this.floatingChatObserver) {
      this.floatingChatObserver.disconnect();
      this.floatingChatObserver = null;
    }
    if (this.floatingChatInterval) {
      if (this.floatingChatHideUsesIdle && 'cancelIdleCallback' in window) window.cancelIdleCallback(this.floatingChatInterval);
      else clearTimeout(this.floatingChatInterval);
      this.floatingChatInterval = null;
      this.floatingChatHideUsesIdle = false;
    }
    this.restoreFloatingChats();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

function patchCartDrawerClass(CartDrawerClass) {
  Object.getOwnPropertyNames(CartDrawer.prototype).forEach((methodName) => {
    if (methodName !== 'constructor') {
      CartDrawerClass.prototype[methodName] = CartDrawer.prototype[methodName];
    }
  });
}

const existingCartDrawer = customElements.get('cart-drawer');
if (existingCartDrawer) {
  patchCartDrawerClass(existingCartDrawer);
} else {
  customElements.define('cart-drawer', CartDrawer);
}

function initializeCartDrawers() {
  document.querySelectorAll('cart-drawer').forEach((drawer) => {
    drawer.floatingChatObserver = drawer.floatingChatObserver || null;
    drawer.floatingChatInterval = drawer.floatingChatInterval || null;
    drawer.floatingChatHideUsesIdle = drawer.floatingChatHideUsesIdle || false;
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
    drawer.setHeaderCartIconAccessibility?.();
    drawer.normalizeCheckoutButton?.();
    drawer.setupDrawerAddButtons?.();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartDrawers, { once: true });
} else {
  initializeCartDrawers();
}

function defineCartDrawerItems() {
  if (customElements.get('cart-drawer-items') || typeof CartItems === 'undefined') return;

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

defineCartDrawerItems();
window.addEventListener('load', defineCartDrawerItems, { once: true });
